import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ConfirmDialog from "components/common/ConfirmDialog";
import SelectableCard from "./SelectableCard";
import { INPATIENT_EVENT_OPTIONS } from "./inpatient";

const filterEventOptions = createFilterOptions();

/** Keep a typed name in the list when it is not one of the suggestions. */
const filterEventsWithCustom = (options, params) => {
  const filtered = filterEventOptions(options, params);
  const inputValue = String(params.inputValue || "").trim();
  if (
    inputValue &&
    !options.some((option) => option.toLowerCase() === inputValue.toLowerCase())
  ) {
    filtered.push(inputValue);
  }
  return filtered;
};

/** The event and its comment, shared by the add form and the edit dialog. */
const EventFields = ({ control, autoFocus = false, commentRows = 2 }) => (
  <>
    <Controller
      name="name"
      control={control}
      render={({ field }) => (
        <Autocomplete
          freeSolo
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          options={INPATIENT_EVENT_OPTIONS}
          filterOptions={filterEventsWithCustom}
          value={field.value || null}
          inputValue={field.value || ""}
          onChange={(_, value) => field.onChange(typeof value === "string" ? value : value || "")}
          onInputChange={(_, value, reason) => {
            if (reason === "reset") return;
            field.onChange(value);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus={autoFocus}
              label="Event"
              placeholder="Choose or type a custom event"
            />
          )}
        />
      )}
    />
    <Controller
      name="comment"
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          label="Comment"
          placeholder="What happened and what was done"
          multiline
          minRows={commentRows}
          fullWidth
        />
      )}
    />
  </>
);

/**
 * Edit an event's name and comment, or remove it.
 * `event` is held by the caller until the dialog has finished closing (see EventsStep), so the
 * fields never empty out mid-fade.
 */
const EditEventDialog = ({ open, event, onClose, onExited, onSave, onRemove }) => {
  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: { name: "", comment: "" },
  });
  const name = watch("name");

  useEffect(() => {
    if (open && event) reset({ name: event.name, comment: event.comment || "" });
  }, [open, event, reset]);

  const submit = handleSubmit(({ name: value, comment }) => {
    onSave(event.id, { name: value.trim(), comment });
    onClose();
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth TransitionProps={{ onExited }}>
      <DialogTitle>Edit event</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <EventFields control={control} commentRows={3} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="error" onClick={() => onRemove(event)} sx={{ mr: "auto" }}>
          Remove
        </Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!String(name || "").trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Inpatient only. Events the clinician documents during the stay: an inline form to add one,
 * then a card per event with a pencil to edit it.
 *
 * On the Events note type this is the first tab after Continue. Specialty / Visit type / Note
 * type were already chosen on setup, so they are not repeated here.
 */
const EventsStep = ({
  events,
  onAddEvent,
  onUpdateEvent,
  onRemoveEvent,
  eventsNote = false,
}) => {
  // Each dialog's event is kept until its close animation ends, and only `open` flips on close,
  // so neither dialog's content changes while it fades out.
  const [editing, setEditing] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: { name: "", comment: "" },
  });
  const name = watch("name");

  const add = handleSubmit(({ name: value, comment }) => {
    if (!String(value || "").trim()) return;
    onAddEvent(value, comment);
    reset({ name: "", comment: "" });
  });

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 1.5 }}>
          {eventsNote ? "Event" : "Events"}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {eventsNote
            ? "Document the events this note covers. Adding an event continues to Diagnosis; the event text is the source for Diagnosis, Plan, and Chart."
            : "Optional. Document anything that happened during the stay; each event goes in the note. Add the time in the comment when it matters."}
        </Typography>
      </Box>

      <Stack component="form" spacing={2} onSubmit={add} sx={{ mb: 4 }}>
        <EventFields control={control} />
        <Button
          type="submit"
          variant="contained"
          disabled={!String(name || "").trim()}
          sx={{ alignSelf: "flex-end" }}
        >
          Add Event
        </Button>
      </Stack>

      {events.map((event) => (
        <SelectableCard
          key={event.id}
          title={event.name}
          selectionVariant="none"
          onEdit={() => {
            setEditing(event);
            setEditOpen(true);
          }}
          editTooltip="Edit event"
          additionalContent={
            event.comment?.trim() ? (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {event.comment}
              </Typography>
            ) : null
          }
        />
      ))}

      <EditEventDialog
        open={editOpen}
        event={editing}
        onClose={() => setEditOpen(false)}
        onExited={() => setEditing(null)}
        onSave={onUpdateEvent}
        onRemove={(event) => {
          setEditOpen(false);
          setRemoving(event);
          setRemoveOpen(true);
        }}
      />

      <ConfirmDialog
        open={removeOpen}
        onClose={() => setRemoveOpen(false)}
        onExited={() => setRemoving(null)}
        title="Remove this event?"
        message={removing ? `${removing.name} and its comment will be removed from the note.` : ""}
        confirmLabel="Remove"
        confirmColor="error"
        onConfirm={() => {
          onRemoveEvent(removing.id);
          setRemoveOpen(false);
        }}
      />
    </Box>
  );
};

export default EventsStep;
